import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { ErrorStateService } from '../core/error-state.service';
import { REQUEST_ID_HEADER, ThirdPartyIntegrationsService } from '../core/third-party-integrations.service';

//
// 1:1 port of the errorInterceptor factory in gmr-invantive.js. Fires globally for every failed
// $http/HttpClient call, independently of whatever .catch()/showHttpGetError handling a specific
// call site also does - both write into the same ErrorStateService signal, matching the original's
// two independent jQuery-DOM-writing paths.
//
let preventFurtherRequests = false;

const HANDLED_STATUS_CODES = [204, 400, 401, 403, 404, 409, 500];

export const errorInterceptor: HttpInterceptorFn = (req, next) =>
{
  //
  // @ngx-translate/http-loader fetches translation JSON via this same HttpClient. Injecting
  // TranslateService below for THAT request would re-enter TranslateService's own construction
  // (it depends on the loader, which depends on HttpClient, which runs through this interceptor)
  // - a genuine circular dependency (NG0200), not present in the AngularJS original because
  // angular-translate's loader didn't share $http's interceptor pipeline this way. Skipping the
  // interceptor for i18n asset requests breaks the cycle; these aren't backend API calls this
  // error-handling logic is meant to cover anyway.
  //
  if (req.url.includes('assets/i18n/'))
  {
    return next(req);
  }

  const translate = inject(TranslateService);
  const errorState = inject(ErrorStateService);
  const thirdParty = inject(ThirdPartyIntegrationsService);

  //
  // Original AngularJS request-interceptor returned `undefined` to "block" further requests once
  // a fatal error occurred, which is not a valid return value here (or, strictly, there either -
  // it would have broken $http's own interceptor chain). The clear intent - stop issuing requests
  // after a fatal error - is preserved by short-circuiting with an error instead.
  //
  if (preventFurtherRequests)
  {
    return throwError(() => new Error('Further requests suppressed after a fatal error.'));
  }

  return next(req).pipe(
    catchError(error =>
    {
      if (!(error instanceof HttpErrorResponse) || !HANDLED_STATUS_CODES.includes(error.status))
      {
        //
        // A status this application has no message for - most often 0 (connection dropped, or the
        // request blocked by an extension) or a gateway 502/503/504. It gets no error dialog, which
        // is the existing behaviour and stays, but it used to get no telemetry either: a backend
        // outage produced nothing at all in Inspectlet or Matomo, at the one moment that hurts.
        //
        if (error instanceof HttpErrorResponse)
        {
          thirdParty.reportUnhandledHttpFailure({ method: req.method, url: error.url ?? req.urlWithParams, status: error.status, requestId: error.headers.get(REQUEST_ID_HEADER) });
        }

        return throwError(() => error);
      }

      return translate.get(['itgen_gmr_oops', 'itgen_gmr_close_window', 'itgen_gmr_error']).pipe(
        switchMap(translations =>
        {
          preventFurtherRequests = true;

          const errorData = error.error;
          errorState.showFromInterceptor(errorData, translations['itgen_gmr_error'], error.headers.get(REQUEST_ID_HEADER));
          thirdParty.reportInterceptorError(errorData, { method: req.method, url: error.url ?? req.urlWithParams, status: error.status, requestId: error.headers.get(REQUEST_ID_HEADER) });

          return throwError(() => error);
        })
      );
    })
  );
};
